import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { TipoMail } from 'src/app/pages/configuracion/Envios/envios/models/tipo-mail';
import { FormularioEnvioService } from 'src/app/pages/configuracion/Envios/envios/services/formulario-envio.service';
import { FormularioValidacionesService } from 'src/app/pages/configuracion/Envios/envios/services/formulario-validaciones.service';
import { MODO_ENVIO_SIMPLE } from 'src/app/pages/configuracion/Envios/documentos/services/modo-envio-simple.token';
import { RecuperaConfiguracionAplicacionApiService } from 'src/app/pages/configuracion/configuracion-rlt/services/recupera-configuracion-aplicacion-api.service';
import { ModalMessageComponent } from 'src/app/shared/components/modals/modal-message/modal-message.component';
import { SpinnerModalSqpComponent } from 'src/app/shared/components/sqp-components/sqpModales/spinner-modal-sqp/spinner-modal-sqp.component';
import { BreadcrumbsService } from 'src/app/shared/services/breadcrumbs.service';
import { CanalesEnvioSimple } from '../../models/firmante-envio-simple';
import { EnvioSimpleFlowService } from '../../services/envio-simple-flow.service';
import { EnvioSimpleProcesamientoApiService } from '../../services/envio-simple-procesamiento-api.service';
import { esTelefonoValido, telefonoEstaVacio } from '../../utils/telefono-validacion.util';
import {
  codigosConEmailDuplicado,
  codigosConTelefonoDuplicado,
  firmantesTienenEmailDuplicado,
  firmantesTienenTelefonoDuplicado
} from '../../utils/contacto-duplicado.util';

/** Paso Envio del flujo Envio simple: layout de firmantes, mensaje y ajustes. */
@Component({
  selector: 'app-envio-simple-envio-page',
  templateUrl: './envio-simple-envio-page.component.html',
  styleUrls: ['./envio-simple-envio-page.component.css'],
  providers: [{ provide: MODO_ENVIO_SIMPLE, useValue: true }]
})
export class EnvioSimpleEnvioPageComponent implements OnInit, OnDestroy {
  readonly emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  forzarValidacionContacto = false;
  codigosEmailDuplicado: string[] = [];
  codigosTelefonoDuplicado: string[] = [];
  enviando = false;
  /** Paridad dosier: SMS solo activable si ModuloSmsActivo. */
  moduloSmsActivo = false;
  tiposMails: TipoMail[] = [];

  constructor(
    public envioSimpleFlowService: EnvioSimpleFlowService,
    private formularioEnvioService: FormularioEnvioService,
    private formularioValidacionesService: FormularioValidacionesService,
    private breadcrumbsService: BreadcrumbsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private procesamientoApiService: EnvioSimpleProcesamientoApiService,
    private recuperaConfiguracionAplicacionService: RecuperaConfiguracionAplicacionApiService
  ) {}

  get envioParalelo(): boolean {
    return this.envioSimpleFlowService.ajustesEnvioEnvio.envioParalelo;
  }

  get canalCorreoActivo(): boolean {
    return this.envioSimpleFlowService.canalesEnvio.envioMail;
  }

  get canalSMSActivo(): boolean {
    return this.moduloSmsActivo && this.envioSimpleFlowService.canalesEnvio.envioSMS;
  }

  get mostrarAvisoCanales(): boolean {
    return !this.canalCorreoActivo && !this.canalSMSActivo;
  }

  get mostrarAvisoEmailInvalido(): boolean {
    return this.canalCorreoActivo && this.tieneFirmantesConEmailInvalido();
  }

  get mostrarAvisoTelefonoInvalido(): boolean {
    return this.canalSMSActivo && this.tieneFirmantesConTelefonoInvalido();
  }

  /** Boton Enviar habilitado solo cuando el envio es posible (1.12). */
  get puedeEnviar(): boolean {
    if (this.enviando) {
      return false;
    }
    return this.cumpleCondicionesEnvio();
  }

  establecerEnvioParalelo(paralelo: boolean): void {
    const ajustes = this.envioSimpleFlowService.ajustesEnvioEnvio;
    this.envioSimpleFlowService.actualizarAjustesEnvio({ ...ajustes, envioParalelo: paralelo });
    this.formularioEnvioService.formularioEnvio
      ?.get('envioParalelo')
      ?.setValue(paralelo, { emitEvent: false });
  }

  toggleCanalCorreo(checked: boolean): void {
    this.actualizarCanales({
      envioMail: checked,
      envioSMS: this.moduloSmsActivo && this.envioSimpleFlowService.canalesEnvio.envioSMS
    });
  }

  toggleCanalSMS(checked: boolean): void {
    if (!this.moduloSmsActivo) {
      this.actualizarCanales({ envioMail: this.canalCorreoActivo, envioSMS: false });
      return;
    }
    this.actualizarCanales({ envioMail: this.canalCorreoActivo, envioSMS: checked });
  }

  cambiarOrdenMail(codigo: string): void {
    this.actualizarCanales({ ordenMail: codigo });
  }

  cambiarOrdenTelefonoSMS(codigo: string): void {
    this.actualizarCanales({ ordenTelefonoSMS: codigo });
  }

  limpiarMarcasEmailDuplicado(): void {
    this.codigosEmailDuplicado = [];
  }

  limpiarMarcasTelefonoDuplicado(): void {
    this.codigosTelefonoDuplicado = [];
  }

  ngOnInit(): void {
    if (!this.envioSimpleFlowService.tieneDocumentos) {
      this.router.navigate(['/panel/envio-simple/documentos']);
      return;
    }
    this.envioSimpleFlowService.establecerPaso(2);
    this.envioSimpleFlowService.prepararPasoEnvio();
    this.breadcrumbsService.addBreadcrumbs('Envío simple');
    this.cargarModuloSmsActivo();
    void this.cargarTiposMails();
  }

  private async cargarTiposMails(): Promise<void> {
    if (!this.formularioEnvioService.tiposMails?.length) {
      await this.formularioEnvioService.getTipoMail();
    }
    this.tiposMails = [...(this.formularioEnvioService.tiposMails ?? [])];
  }

  private cargarModuloSmsActivo(): void {
    this.recuperaConfiguracionAplicacionService
      .getModulosActivos()
      .pipe(
        take(1),
        catchError(() => of({ ModuloSmsActivo: false, ModuloEnvioXmlActivo: false }))
      )
      .subscribe(modulos => {
        this.moduloSmsActivo = !!modulos?.ModuloSmsActivo;
        if (!this.moduloSmsActivo && this.envioSimpleFlowService.canalesEnvio.envioSMS) {
          this.actualizarCanales({
            envioMail: this.envioSimpleFlowService.canalesEnvio.envioMail,
            envioSMS: false
          });
        }
      });
  }

  volver(): void {
    this.persistirEstadoPasoEnvio();
    this.envioSimpleFlowService.establecerPaso(1);
    this.router.navigate(['/panel/envio-simple/firmantes']);
  }

  ngOnDestroy(): void {
    this.persistirEstadoPasoEnvio();
  }

  /** Si Enviar esta deshabilitado, informa el motivo (mismo patron que paso Firmantes). */
  onEnviarClick(): void {
    if (this.puedeEnviar || this.enviando) {
      return;
    }
    this.persistirEstadoPasoEnvio();
    this.activarValidacionesVisualesEnvio();
    this.marcarContactosDuplicadosSiAplica();
    this.mostrarError(this.obtenerMensajeBloqueoEnvio());
  }

  enviar(): void {
    if (this.enviando) {
      return;
    }

    this.persistirEstadoPasoEnvio();
    this.activarValidacionesVisualesEnvio();
    const estadoPasoEnvio = this.envioSimpleFlowService.exportarEstadoPasoEnvio();
    // eslint-disable-next-line no-console
    console.log('[ENVIO-SIMPLE][click Enviar][estado]', estadoPasoEnvio);
    if (!this.validarPrevioEnvio()) {
      return;
    }

    this.enviando = true;
    const spinnerRef: MatDialogRef<SpinnerModalSqpComponent> = this.dialog.open(SpinnerModalSqpComponent, {
      disableClose: true
    });
    const estadoFlujo = this.envioSimpleFlowService.exportarEstadoPasoEnvio();
    this.procesamientoApiService.procesar(estadoFlujo).subscribe({
      next: resultado => {
        spinnerRef.close();
        this.envioSimpleFlowService.establecerResultadoConfirmacion(resultado);
        this.envioSimpleFlowService.establecerPaso(3);
        this.enviando = false;
        this.router.navigate(['/panel/envio-simple/confirmacion']);
      },
      error: () => {
        spinnerRef.close();
        this.enviando = false;
        this.mostrarError('No se ha podido procesar el envío. Inténtalo de nuevo.');
      }
    });
  }

  private actualizarCanales(canales: Partial<CanalesEnvioSimple>): void {
    this.envioSimpleFlowService.actualizarCanales(canales);
    const actuales = this.envioSimpleFlowService.canalesEnvio;
    this.formularioEnvioService.formularioEnvio?.get('envioMail')?.setValue(actuales.envioMail, { emitEvent: false });
    this.formularioEnvioService.formularioEnvio?.get('envioSMS')?.setValue(actuales.envioSMS, { emitEvent: false });
  }

  private persistirEstadoPasoEnvio(): void {
    const formulario = this.formularioEnvioService.formularioEnvio;
    if (!formulario) {
      return;
    }

    const form = formulario.getRawValue();
    const canales = this.envioSimpleFlowService.canalesEnvio;
    this.envioSimpleFlowService.persistirEstadoPasoEnvio({
      mensajeCorreo: {
        subject: form.subject ?? '',
        body: form.body ?? '',
        bodySMS: form.bodySMS ?? ''
      },
      ajustesEnvio: {
        recordatorio: !!form.recordatorio,
        recordatorios: [...this.formularioEnvioService.recordatorios],
        caducidad: form.caducidad ?? 365,
        envioParalelo: !!form.envioParalelo
      },
      canales: {
        envioMail: canales.envioMail,
        envioSMS: canales.envioSMS,
        ordenMail: canales.ordenMail,
        ordenTelefonoSMS: canales.ordenTelefonoSMS
      }
    });
  }

  private tieneFirmantesConEmailInvalido(): boolean {
    return this.envioSimpleFlowService.firmantesEnvioList.some(firmante => {
      const email = firmante.email?.trim() ?? '';
      return email.length === 0 || !this.emailRegex.test(email);
    });
  }

  private tieneFirmantesConTelefonoInvalido(): boolean {
    return this.envioSimpleFlowService.firmantesEnvioList.some(firmante => {
      return !esTelefonoValido(firmante.telefono);
    });
  }

  /** Condiciones silenciosas para habilitar Enviar (sin snackbars ni modales). */
  private cumpleCondicionesEnvio(): boolean {
    if (!this.envioSimpleFlowService.tieneDocumentos) {
      return false;
    }
    if (!this.hayFirmantePrincipalConfigurado()) {
      return false;
    }
    if (!this.canalCorreoActivo && !this.canalSMSActivo) {
      return false;
    }
    if (!this.validarDatosContactoPorCanal()) {
      return false;
    }
    if (this.hayContactosDuplicadosPorCanal()) {
      return false;
    }
    if (!this.cumpleMensajeYAjustes()) {
      return false;
    }
    if (!this.envioSimpleFlowService.algunDocumentoConFirmaPrincipalCompleta()) {
      return false;
    }
    return true;
  }

  private hayContactosDuplicadosPorCanal(): boolean {
    const firmantes = this.envioSimpleFlowService.firmantesEnvioList;
    if (this.canalCorreoActivo && firmantesTienenEmailDuplicado(firmantes)) {
      return true;
    }
    if (this.canalSMSActivo && firmantesTienenTelefonoDuplicado(firmantes)) {
      return true;
    }
    return false;
  }

  private cumpleMensajeYAjustes(): boolean {
    const form = this.formularioEnvioService.formularioEnvio;
    if (!form) {
      return false;
    }
    const bodySMS = form.get('bodySMS')?.value as string | null;
    if ((bodySMS?.length ?? 0) > 110) {
      return false;
    }
    if (this.formularioValidacionesService.validacionAvisos()) {
      return false;
    }
    const caducidad = form.get('caducidad');
    if (!!caducidad && caducidad.invalid) {
      return false;
    }
    return true;
  }

  private obtenerMensajeBloqueoEnvio(): string {
    if (!this.envioSimpleFlowService.tieneDocumentos) {
      return 'Falta al menos un documento para enviar.';
    }
    if (!this.hayFirmantePrincipalConfigurado()) {
      return 'Falta la configuración del firmante principal.';
    }
    if (!this.canalCorreoActivo && !this.canalSMSActivo) {
      return 'Debes seleccionar un canal de notificación y completar los datos de los firmantes.';
    }
    if (!this.validarDatosContactoPorCanal()) {
      return 'Revisa los datos de contacto de los firmantes para continuar con el envío.';
    }
    const firmantes = this.envioSimpleFlowService.firmantesEnvioList;
    if (this.canalCorreoActivo && firmantesTienenEmailDuplicado(firmantes)) {
      return 'No puede haber dos firmantes con el mismo e-mail cuando el canal de correo está activo.';
    }
    if (this.canalSMSActivo && firmantesTienenTelefonoDuplicado(firmantes)) {
      return 'No puede haber dos firmantes con el mismo teléfono cuando el canal SMS está activo.';
    }
    const form = this.formularioEnvioService.formularioEnvio;
    const bodySMS = form?.get('bodySMS')?.value as string | null;
    if ((bodySMS?.length ?? 0) > 110) {
      return 'El mensaje de SMS supera el máximo de 110 carácteres.';
    }
    if (this.formularioValidacionesService.validacionAvisos()) {
      return 'Revisa los recordatorios configurados antes de enviar.';
    }
    const caducidad = form?.get('caducidad');
    if (!!caducidad && caducidad.invalid) {
      return 'La caducidad debe estar entre 1 y 365 días.';
    }
    if (!this.envioSimpleFlowService.algunDocumentoConFirmaPrincipalCompleta()) {
      return 'Debe existir al menos un documento con firma del firmante principal para enviar.';
    }
    return 'Faltan datos por completar antes de enviar.';
  }

  private marcarContactosDuplicadosSiAplica(): void {
    const firmantes = this.envioSimpleFlowService.firmantesEnvioList;
    this.codigosEmailDuplicado = [];
    this.codigosTelefonoDuplicado = [];
    if (this.canalCorreoActivo && firmantesTienenEmailDuplicado(firmantes)) {
      this.codigosEmailDuplicado = codigosConEmailDuplicado(firmantes);
    }
    if (this.canalSMSActivo && firmantesTienenTelefonoDuplicado(firmantes)) {
      this.codigosTelefonoDuplicado = codigosConTelefonoDuplicado(firmantes);
    }
  }

  private validarPrevioEnvio(): boolean {
    if (!this.envioSimpleFlowService.tieneDocumentos) {
      this.mostrarModalError(
        'Error de validación',
        'Falta al menos un documento para enviar.'
      );
      return false;
    }
    if (!this.hayFirmantePrincipalConfigurado()) {
      this.mostrarModalError(
        'Error de validación',
        'Falta la configuración del firmante principal.'
      );
      return false;
    }
    if (!this.validarCanalNotificacion()) {
      return false;
    }
    if (!this.validarDatosContactoPorCanal()) {
      this.mostrarError('Revisa los datos de contacto de los firmantes para continuar con el envío.');
      return false;
    }
    if (!this.validarContactosDuplicadosPorCanal()) {
      return false;
    }
    if (!this.validarErroresMensajeYAjustes()) {
      return false;
    }
    if (!this.envioSimpleFlowService.algunDocumentoConFirmaPrincipalCompleta()) {
      this.mostrarError(
        'Debe existir al menos un documento con firma del firmante principal para enviar.'
      );
      return false;
    }
    return true;
  }

  private activarValidacionesVisualesEnvio(): void {
    this.forzarValidacionContacto = true;
    this.formularioEnvioService.formularioEnvio.markAllAsTouched();
    this.formularioValidacionesService.validacionAvisos();
    this.formularioEnvioService.formularioEnvio.updateValueAndValidity({ emitEvent: false });
  }

  private hayFirmantePrincipalConfigurado(): boolean {
    return this.envioSimpleFlowService.firmantesEnvioList.some(firmante => firmante.esPrincipal);
  }

  private validarCanalNotificacion(): boolean {
    if (!this.canalCorreoActivo && !this.canalSMSActivo) {
      this.mostrarError(
        'Debes seleccionar un canal de notificación y completar los datos de los firmantes.'
      );
      return false;
    }
    return true;
  }

  private validarDatosContactoPorCanal(): boolean {
    const firmantes = this.envioSimpleFlowService.firmantesEnvioList;
    if (this.canalCorreoActivo) {
      const hayEmailInvalido = firmantes.some(firmante => {
        const email = firmante.email?.trim() ?? '';
        return email.length === 0 || !this.emailRegex.test(email);
      });
      if (hayEmailInvalido) {
        return false;
      }
    }

    if (this.canalSMSActivo) {
      const hayTelefonoInvalido = firmantes.some(firmante => {
        return telefonoEstaVacio(firmante.telefono) || !esTelefonoValido(firmante.telefono);
      });
      if (hayTelefonoInvalido) {
        return false;
      }
    }
    return true;
  }

  private validarContactosDuplicadosPorCanal(): boolean {
    const firmantes = this.envioSimpleFlowService.firmantesEnvioList;
    this.codigosEmailDuplicado = [];
    this.codigosTelefonoDuplicado = [];

    if (this.canalCorreoActivo && firmantesTienenEmailDuplicado(firmantes)) {
      this.codigosEmailDuplicado = codigosConEmailDuplicado(firmantes);
      this.mostrarError('No puede haber dos firmantes con el mismo e-mail cuando el canal de correo está activo.');
      return false;
    }

    if (this.canalSMSActivo && firmantesTienenTelefonoDuplicado(firmantes)) {
      this.codigosTelefonoDuplicado = codigosConTelefonoDuplicado(firmantes);
      this.mostrarError('No puede haber dos firmantes con el mismo teléfono cuando el canal SMS está activo.');
      return false;
    }

    return true;
  }

  private validarErroresMensajeYAjustes(): boolean {
    const form = this.formularioEnvioService.formularioEnvio;
    const bodySMS = form.get('bodySMS')?.value as string | null;
    if ((bodySMS?.length ?? 0) > 110) {
      this.mostrarError('El mensaje de SMS supera el máximo de 110 carácteres.');
      return false;
    }

    if (this.formularioValidacionesService.validacionAvisos()) {
      this.mostrarError('Revisa los recordatorios configurados antes de enviar.');
      return false;
    }

    const caducidad = form.get('caducidad');
    if (!!caducidad && caducidad.invalid) {
      this.mostrarError('La caducidad debe estar entre 1 y 365 días.');
      return false;
    }
    return true;
  }

  private mostrarError(mensaje: string): void {
    this.snackBar.open(mensaje, 'Cerrar', { duration: 4000 });
  }

  private mostrarModalError(titulo: string, mensaje: string): void {
    this.dialog.open(ModalMessageComponent, {
      data: {
        titulo,
        mensaje,
        alert: true
      }
    });
  }
}
